import { useEffect } from 'react'
import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal, useSignalEffect } from '@preact/signals-react'

import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Table from 'react-bootstrap/Table'

import CopyRowAsJSON from '../../components/CopyRowAsJSON.jsx'
import CreateConfigTemplateModal from './components/CreateConfigTemplateModal.jsx'
import DebugAccordion from '../../components/DebugAccordion'
import DeleteConfigTemplateModal from './components/DeleteConfigTemplateModal.jsx'
import Icon from '../../components/Icon'
import Loadable from '../../components/Loadable.jsx'
import LoadingAnimation from '../../components/LoadingAnimation.jsx'
import PageSizeSelector from '../../components/PageSizeSelector.jsx'
import PageTitle from '../../components/PageTitle.jsx'

import { NotificationTypes, addNotification } from '../../state/actions/notificationActions'
import { fetchConfigTemplateList, createConfigTemplate, deleteConfigTemplate } from '../../state/actions/configTemplateActions'
import { fetchUtgManifest } from '../../state/actions/utgManifestActions'

import { configTemplateListSignal, configTemplateListLoadedSignal, configTemplateListErrorSignal } from '../../state/signals/configTemplateSignals'
import { utgManifestSignal } from '../../state/signals/utgManifestSignals'

import { SortDirectionType } from '../../utils/constants'
import { computeSortedData } from '../../utils/table.js'
import { scrollToTop } from '../../utils/utils.js'

export default function ConfigTemplateList() {

    const navigate = useNavigate();
    const { beId } = useParams()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { config, accessToken, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewConfigTemplates) {
            addNotification('You are not authorized to view UTG Config Templates for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditConfigTemplates
    })

    useEffect(() => {
        fetchConfigTemplateList(beId, config, accessToken)
        fetchUtgManifest(config, accessToken)
    }, [beId, config, accessToken])

    const currentPageSignal = useSignal(1)
    const rowsPerPageSignal = useSignal(parseInt(window.localStorage.getItem('pagesize'), 10) || 250)
    const filterStringSignal = useSignal("")
    const sortColumnSignal = useSignal('')

    const sortDirectionSignal = useSignal(SortDirectionType.DESCENDING)

    useSignalEffect(() => {
        window.localStorage.setItem('pagesize', rowsPerPageSignal.value)
    })

    const filteredDataComputed = useComputed(() => {
        if (!filterStringSignal.value) {
            return configTemplateListSignal.value ? [...configTemplateListSignal.value] : []
        }

        // Convert the filter to lowercase
        const lowerCaseFilter = filterStringSignal.value.toLowerCase()

        // Filter the data based on the filter text
        return configTemplateListSignal.value.filter(item => {

            // Limit search to these fields
            let searchData = {
                name: item.name
            }

            return Object.values(searchData).some(value => {
                if (value && (typeof (value) == 'string')) {
                    return value.toString().toLowerCase().includes(lowerCaseFilter)
                }
            })
        })
    })

    const sortedDataComputed = useComputed(computeSortedData(filteredDataComputed, sortColumnSignal, sortDirectionSignal))

    const totalPagesComputed = useComputed(() => {
        return Math.ceil(filteredDataComputed.value.length / parseInt(rowsPerPageSignal.value, 10))
    })

    const displayDataComputed = useComputed(() => {
        // Calculate the starting and ending indexes for pagination
        const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value
        return sortedDataComputed.value.slice(startIndex, endIndex)
    })
    const startDisplayRowNumberComputed = useComputed(() => {
        return ((currentPageSignal.value - 1) * rowsPerPageSignal.value) + 1
    })
    const endDisplayRowNumberComputed = useComputed(() => {
        const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value
        return endIndex < filteredDataComputed.value.length ? endIndex : filteredDataComputed.value.length
    })

    const accordionData = [
        { name: 'Config Templates', data: configTemplateListSignal.value },
        { name: 'Display Data', data: displayDataComputed.value },
        { name: 'Sorted Data', data: sortedDataComputed.value },
        { name: 'Filtered Data', data: filteredDataComputed.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    // Delete profile modal
    const showModalDeleteSignal = useSignal(false)
    const deleteConfigTemplateItemSignal = useSignal({})
    const handleDeleteConfigTemplateShowModal = item => {
        if (item) {
            deleteConfigTemplateItemSignal.value = item
            showModalDeleteSignal.value = true
        }
    }
    const handleSubmitModalDelete = async () => {
        configTemplateListLoadedSignal.value = false
        showModalDeleteSignal.value = false
        deleteConfigTemplate(beId, deleteConfigTemplateItemSignal.value.id, config, accessToken).then(
            () => {
                addNotification(`Deleted config template "${deleteConfigTemplateItemSignal.value.name}"`, NotificationTypes.SUCCESS, true, "Deletion successful")
                scrollToTop("main")
                fetchConfigTemplateList(beId, config, accessToken)
            }
        ).catch(
            (error) => {
                addNotification(`Unable to delete profile. ${error}`)
            }
        )
    }

    // New config template modal
    const showModalNewSignal = useSignal(false)
    const newConfigTemplateNameSignal = useSignal('') // server enforces uniqueness within be
    const newConfigTemplateVersionSignal = useSignal('')

    const handleSubmitModalNew = async () => {
        showModalNewSignal.value = false
        const configTemplate = {
            name: newConfigTemplateNameSignal.value,
            businessEntityId: beId,
            config: JSON.stringify({}),
            utgSchemaVersion: newConfigTemplateVersionSignal.value
        }
        createConfigTemplate(beId, configTemplate, config, accessToken).then(
            () => {
                addNotification('Config Template successfully created', 'success', true)
                fetchConfigTemplateList(beId, config, accessToken)
            }
        ).catch(
            (error) => {
                addNotification(`Unable to create config template. ${error}`)
            }
        )
    }

    // Action toolbar

    const handlePageSizeSelect = (v) => {
        currentPageSignal.value = 1
        rowsPerPageSignal.value = parseInt(v, 10)
    }
    const handleFilterStringChange = (e) => {
        currentPageSignal.value = 1
        filterStringSignal.value = e.currentTarget.value
    }

    const handleHeaderTableClick = datafield => {
        // if already sorted on this column then toggle sort direction
        if (datafield == sortColumnSignal.value) {
            sortDirectionSignal.value = sortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
            return
        }

        // update sort column - the computed functions will then run and render the sorted data
        sortColumnSignal.value = datafield
    }

    function TableRows({ items }) {
        if (!configTemplateListLoadedSignal.value) {
            return <tr><td colSpan={3}><LoadingAnimation /></td></tr>
        }
        if (items && items.length) {
            const rows = items.map(item => {
                return <TableRow item={item} key={`${item.id}`} />
            })

            return rows
        }
        else if (items) {
            return <tr><td colSpan="23" className="text-center">No config templates found</td></tr>
        }
    }

    function TableRow({ item }) {
        return (
            <tr>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions">
                            <Icon icon="gear" height={16} width={16}></Icon>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <CopyRowAsJSON item={item} />
                            {
                                canEditComputed.value && (
                                    <Dropdown.Item onClick={() => handleDeleteConfigTemplateShowModal(item)}>
                                        <Icon icon="trash" width={16} height={16}></Icon>
                                        &nbsp;Delete config template
                                    </Dropdown.Item>
                                )
                            }
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
                <td><Link to={`./${item.id}`}>{item.name}</Link></td>
                <td>{item.utgSchemaVersion}</td>
                <td>{item.businessEntityId}</td>
            </tr>
        )
    }

    function PaginationItems() {
        let items = []
        for (let i = 1; i <= totalPagesComputed.value; i++) {
            items.push(
                <Pagination.Item key={i} active={i === currentPageSignal.value} onClick={() => currentPageSignal.value = i}>{i}</Pagination.Item>
            )
        }
        return items
    }

    function TableColumnHeader({ title, sortable, datafield }) {
        // non-sortable columns do not have click handlers
        if (!sortable) {
            return (
                <th>{title}</th>
            )
        }

        // sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
        if (datafield != sortColumnSignal.value) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
            )
        }

        // datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
        if (sortDirectionSignal.value == SortDirectionType.DESCENDING) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                    <Icon icon="caret-down" width="16" height="16"></Icon>
                </th>
            )
        }

        // else must be ascending
        return (
            <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                <Icon icon="caret-up" width="16" height="16"></Icon>
            </th>
        )
    }

    return (
        <>
            <PageTitle title='Config Template List' canEditSignal={canEditComputed} />
            <Nav className="justify-content-between px-3 py-1 my-2 bg-light">
                <Nav.Item className="me-auto">
                    <PageSizeSelector pageSizeSignal={rowsPerPageSignal} handlePageSizeSelect={handlePageSizeSelect} />
                </Nav.Item>
                <Nav.Item className="me-2">
                    <Form.Control type="text" placeholder="Search" size="sm" htmlSize="75" onChange={handleFilterStringChange} />
                </Nav.Item>
                {canEditComputed.value && <Nav.Item>
                    <Button size="sm" variant="outline-secondary" className="d-flex align-items-center justify-content-center" onClick={() => showModalNewSignal.value = true}><Icon icon='plus-circle' width="16" height="16" ></Icon>&nbsp;New</Button>
                </Nav.Item>}
            </Nav>
            <Table responsive striped hover size="sm" className="cc-table">
                <thead>
                    <tr>
                        <TableColumnHeader title={'Action'} sortable={false}></TableColumnHeader>
                        <TableColumnHeader title={'Config Template Name'} sortable={true} datafield={'name'}></TableColumnHeader>
                        <TableColumnHeader title={'UTG Version'} sortable={true} datafield={'utgSchemaVersion'}></TableColumnHeader>
                        <TableColumnHeader title={'Parent Account'} sortable={true} datafield={'businessEntityId'}></TableColumnHeader>
                    </tr>
                </thead>
                <tbody><TableRows items={displayDataComputed.value} /></tbody>
                <tfoot><tr><td className="fs-6" colSpan={19}>
                    {
                        displayDataComputed.value > 0 && (
                            <>Displaying {startDisplayRowNumberComputed.value}-{endDisplayRowNumberComputed.value} of {filteredDataComputed.value.length}
                            </>
                        )
                    }
                </td></tr></tfoot>
            </Table>
            <Pagination className="px-3 d-flex flex-wrap" size="sm"><PaginationItems /></Pagination>
            <DebugAccordion data={accordionData} debug={debug} />

            <CreateConfigTemplateModal
                showSignal={showModalNewSignal}
                configTemplateListSignal={configTemplateListSignal}
                schemaVersionsSignal={utgManifestSignal}
                newConfigTemplateNameSignal={newConfigTemplateNameSignal}
                newConfigTemplateVersionSignal={newConfigTemplateVersionSignal}
                handleSubmit={handleSubmitModalNew}
            />

            <DeleteConfigTemplateModal
                showSignal={showModalDeleteSignal}
                deleteConfigTemplateSignal={deleteConfigTemplateItemSignal}
                handleSubmit={handleSubmitModalDelete}
            />
        </>
    )
}